<template>
    <BaseDialog v-model:open="isOpen" :title="t('chat.thoughtProcess.title')">
        <div class="flex flex-col gap-4 items-center">
            <div v-if="!isNadiaChatRefreshEnabled" class="shrink-0">
                <CoachingModeMarble is-loading size="big" />
            </div>

            <div v-if="hasError" role="alert" class="shrink-0 body-semibold text-ink-muted">{{ t("chat.thoughtProcess.error") }}</div>
            <div v-else-if="isLoading && isNadiaChatRefreshEnabled" class="shrink-0 flex justify-center">
                <NadiaThinkingShapes :status-text="t('chat.thoughtProcess.loading')" />
            </div>
            <div v-else-if="isLoading" class="shrink-0 flex justify-center">
                <LoadingSpinner class="h-8 text-ink" />
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -- Content is sanitized with DOMPurify before rendering (see computed explanation) -->
            <div v-else-if="!!explanation" class="flex flex-col gap-4 *:whitespace-pre-line text-ink-muted" v-html="explanation"></div>
        </div>
        <template #footer
            ><BaseButton theme="primary" @click="isOpen = false">{{ t("chat.thoughtProcess.dismiss") }}</BaseButton></template
        >
    </BaseDialog>
</template>

<script setup lang="ts">
import type { GlobalProperties } from "~vue/appTypes";
import { ACTION, lineIsAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { useNadiaChatRefreshEnabled } from "~vue/composables/useNadiaChatRefresh";
import type { useSendStreamingEvent } from "~vue/composables/useSendStreamingEvent";
import { CHAT_EVENT } from "~vue/events";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { ChatMessage, ParseActionsGenericAction } from "~vue/types/chat";
import { noImageRenderer, sanitizeForChat } from "~vue/utils/markdown";
import { marked } from "marked";
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import NadiaThinkingShapes from "./NadiaThinkingShapes.vue";

const props = withDefaults(defineProps<{ messages?: ChatMessage[] }>(), {
    messages: () => [],
});

const { emitter, $sendEvent } = inject<GlobalProperties>("globalProperties")!;
const sendStreamingEvent = inject<ReturnType<typeof useSendStreamingEvent> | null>("sendStreamingEvent", null);
const isNadiaChatRefreshEnabled = useNadiaChatRefreshEnabled();
const { t } = useI18n();

const messageId = ref<number | null>(null);
const isLoading = ref(false);
const hasError = ref(false);
const isOpen = ref(false);

watch(isOpen, (value) => {
    if (!value) {
        messageId.value = null;
    }
});

function open(msgId: number) {
    messageId.value = msgId;
    isOpen.value = true;
}

onMounted(() => {
    emitter.on(CHAT_EVENT.OPEN_THOUGHT_PROCESS_DIALOG, open);
});

onUnmounted(() => {
    emitter.off(CHAT_EVENT.OPEN_THOUGHT_PROCESS_DIALOG, open);
});

const message = computed(() => {
    if (!messageId.value) {
        return null;
    }
    const result = props.messages.find((m) => m.chat_message_id === messageId.value);
    return result;
});

const explanation = computed(() => {
    if (!message.value) {
        return null;
    }
    const explanationLine = message.value.lines.find((line): line is ParseActionsGenericAction => lineIsAction(line) && line.action_name === ACTION.LLM_EXPLANATION);
    if (!explanationLine) {
        return null;
    }
    const content = explanationLine.action_params?.content;
    return sanitizeForChat(marked.parse(typeof content === "string" ? content : "", { async: false, extensions: { renderers: noImageRenderer, childTokens: {} } }));
});

watch(
    message,
    (newMessage) => {
        if (!newMessage) {
            return;
        }
        const explanationLine = newMessage.lines.find((line) => lineIsAction(line) && line.action_name === ACTION.LLM_EXPLANATION);
        if (explanationLine) {
            isLoading.value = false;
        } else {
            hasError.value = false;
            isLoading.value = true;
            const payload = {
                chat_message_id: newMessage.chat_message_id,
            };
            const send = sendStreamingEvent ? sendStreamingEvent("explain_llm_response", payload) : $sendEvent("explain_llm_response", payload);
            send.catch((error: unknown) => {
                console.error("Failed to send explain_llm_response event", error);
                hasError.value = true;
            });
        }
    },
    { deep: true },
);
</script>
